import { constructProxyUrl } from '@/utils/proxy'

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.webm']

// Detects whether an asset is a video from its file name
export const getMediaType = (fileName = '') => {
    const lowerName = fileName.toLowerCase()
    return VIDEO_EXTENSIONS.some((ext) => lowerName.includes(ext)) ? 'video' : 'image'
}

// Trim data is stored in milliseconds; the preview players expect seconds
export const convertTrimData = (rawTrimData) =>
    rawTrimData
        ? {
              ...rawTrimData,
              start: rawTrimData.start ? rawTrimData.start / 1000 : null,
              end: rawTrimData.end ? rawTrimData.end / 1000 : null,
          }
        : null

// Maps the stored creative format (IMAGE, singleImage, single_image, VIDEO, CAROUSEL) to the AdPreview format names
export const normalizeCreativeFormat = (format) => {
    const lowerFormat = format?.toLowerCase() || 'singleImage'
    if (lowerFormat === 'image' || lowerFormat === 'singleimage' || lowerFormat === 'single_image') {
        return 'singleImage'
    }
    return lowerFormat
}

// JSON columns of a creative (primary_texts, headlines, descriptions, tags) arrive as a string or an array
export const parseJsonList = (value) => {
    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

// Process assets from creative data into format expected by AdPreview
export const processCreativeAssets = (assets, format) => {
    if (!assets || assets.length === 0) {
        return { assetData: null, croppedImageUrl: null, carouselAssets: [] }
    }

    if (format === 'carousel') {
        const carouselAssets = assets.slice(0, 5).map((asset) => ({
            id: asset.id,
            url: asset.url,
            type: getMediaType(asset.name),
            width: asset.width,
            height: asset.height,
            name: asset.name,
            variations: asset.variations || [],
            headline: asset.headline || '',
            description: asset.description || '',
            trim_data: convertTrimData(asset.trim_data),
            thumbnails: asset.thumbnails,
        }))
        return { assetData: null, croppedImageUrl: null, carouselAssets }
    }

    const firstAsset = assets.find((asset) => asset.variations?.some((v) => v.format === 'square')) || assets[0]
    const proxyUrl = constructProxyUrl(firstAsset.url)

    // Build cropData from variations for getFormatMediaProps (same shape as useAdAssets)
    const cropData =
        firstAsset.variations
            ?.filter((v) => v.cropped && v.crop_data)
            ?.map((v) => ({ name: v.format, ...v.crop_data })) || null

    const assetData = {
        url: proxyUrl,
        type: getMediaType(firstAsset.name),
        width: firstAsset.width,
        height: firstAsset.height,
        variations: firstAsset.variations || [],
        cropData: cropData?.length > 0 ? cropData : null,
        trim_data: convertTrimData(firstAsset.trim_data),
        thumbnails: firstAsset.thumbnails,
    }
    return { assetData, croppedImageUrl: proxyUrl, carouselAssets: [] }
}

// Variation of the given format with its asset's trim data converted to seconds, keeping the crop data
export const getAssetVariantForFormat = (assetVariations, format) => {
    const variant = assetVariations.find((x) => x.format === format)
    if (!variant) return null

    return {
        ...variant,
        adCreativeAsset: {
            ...variant.adCreativeAsset,
            trim_data: convertTrimData(variant.adCreativeAsset?.trim_data),
        },
    }
}
